import type { AuthTokens } from "@/services/types/auth.types";

/**
 * Token Manager
 * Handles storage and retrieval of JWT tokens (accessToken and refreshToken)
 */

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const TOKEN_EXPIRY_KEY = "tokenExpiry";
const REFRESH_TOKEN_EXPIRY_KEY = "refreshTokenExpiry";

/** Safety margin (seconds) subtracted from the access token lifetime. */
const ACCESS_TOKEN_BUFFER_SECONDS = 60;

function isPositiveNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

class TokenManager {
  /**
   * Store access token.
   * `expiresIn` is the token lifetime in SECONDS; it is converted to an absolute
   * timestamp (ms) minus a 60s buffer. Invalid/missing values clear any stale expiry.
   */
  static setAccessToken(token: string, expiresIn?: number | null): void {
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);

      if (isPositiveNumber(expiresIn)) {
        const lifetimeSeconds = Math.max(
          expiresIn - ACCESS_TOKEN_BUFFER_SECONDS,
          0,
        );
        const expiryTime = Date.now() + lifetimeSeconds * 1000;
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      } else {
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
      }
    } catch (error) {
      console.error("Error storing access token:", error);
    }
  }

  /**
   * Get access token
   */
  static getAccessToken(): string | null {
    try {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error("Error retrieving access token:", error);
      return null;
    }
  }

  /**
   * Store refresh token.
   * `refreshExpiresIn` is the lifetime in SECONDS (stored as an absolute ms timestamp, no buffer).
   */
  static setRefreshToken(
    token: string,
    refreshExpiresIn?: number | null,
  ): void {
    try {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);

      if (isPositiveNumber(refreshExpiresIn)) {
        const expiryTime = Date.now() + refreshExpiresIn * 1000;
        localStorage.setItem(REFRESH_TOKEN_EXPIRY_KEY, expiryTime.toString());
      } else {
        localStorage.removeItem(REFRESH_TOKEN_EXPIRY_KEY);
      }
    } catch (error) {
      console.error("Error storing refresh token:", error);
    }
  }

  /**
   * Store a normalized token set (login / refresh).
   * The refresh token is only replaced when the response contains one.
   */
  static setTokens(tokens: AuthTokens): void {
    this.setAccessToken(tokens.accessToken, tokens.expiresIn);
    if (tokens.refreshToken) {
      this.setRefreshToken(tokens.refreshToken, tokens.refreshExpiresIn);
    }
  }

  /**
   * Get refresh token
   */
  static getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error("Error retrieving refresh token:", error);
      return null;
    }
  }

  /**
   * Check if access token is expired
   */
  static isAccessTokenExpired(): boolean {
    try {
      const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
      if (!expiryTime) {
        // If no expiry time is set, check if token exists
        return !this.getAccessToken();
      }
      return Date.now() >= parseInt(expiryTime, 10);
    } catch (error) {
      console.error("Error checking token expiry:", error);
      return true;
    }
  }

  /**
   * Check if the refresh token is known to be expired.
   * Returns false when no refresh expiry was stored (unknown).
   */
  static isRefreshTokenExpired(): boolean {
    try {
      const expiryTime = localStorage.getItem(REFRESH_TOKEN_EXPIRY_KEY);
      if (!expiryTime) {
        return false;
      }
      return Date.now() >= parseInt(expiryTime, 10);
    } catch (error) {
      console.error("Error checking refresh token expiry:", error);
      return false;
    }
  }

  /**
   * Clear all tokens
   */
  static clearTokens(): void {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(REFRESH_TOKEN_EXPIRY_KEY);
    } catch (error) {
      console.error("Error clearing tokens:", error);
    }
  }

  /**
   * Check if user has valid tokens
   */
  static hasValidTokens(): boolean {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!accessToken || !refreshToken) {
      return false;
    }

    // Session is over once the refresh token itself has expired
    if (this.isRefreshTokenExpired()) {
      return false;
    }

    // If access token is expired but refresh token exists, we can still refresh
    if (this.isAccessTokenExpired()) {
      return !!refreshToken;
    }

    return true;
  }
}

export default TokenManager;
